(ns hai.notes.editor
  "The assembled note-editing surfaces: the sticky-yellow paper holding the
   composer, and the shared-with/share footer — as a desktop edit modal with a
   tinted header ([[modal-content]] in [[dialog]]) or a mobile full-screen
   editor ([[screen]]). Hosts supply the note data and save wiring as props —
   the home cards open the editor on a note from their list, the creation
   flow on a fresh standalone note."
  (:require
   [hai.entity.form :as entity-form]
   [hai.entity.kebab :as kebab]
   [hai.entity.sections :as sections]
   [hai.mini-apps.frame :as frame]
   [hai.notes.card :as note-card]
   [hai.notes.form :as form]
   [hai.notes.header :as header]
   [hai.sharing.helper :as sharing]
   [hai.ui.components :as c]
   [hai.ui.icons :as icons]
   [hai.ui.tokens :as t]
   [lambdaisland.ornament :as o]
   [reagent.core :as r]))

(defn body
  "The editing innards every expanded-editor host renders: the composer
   ([[hai.notes.form/note-form]]), with the shared-with / share controls at
   the start of its footer — or, for a viewer without edit rights (`:editing?`
   false), the text and its chips read-only over the share footer. Hosts wrap
   it in their own styled container, which styles `textarea.body`.
   `:draft-id` is the draft the composer edits ([[hai.entity.draft]]) — the
   host seeds it, and hands the same one to two surfaces to make them one
   editing session. `:share?` (default true) gates the share button.
   `:spaces?` (default false) gates the spaces half of the tag row — an
   admin + developer-features feature the host opts into."
  [{:keys [note draft-id editing? share? spaces? auto-focus? show-hint? saving? on-save on-cancel
           on-open-entity on-share-opened on-share-closed]
    :or {editing? true share? true}}]
  (let [{:keys [text urls contacts notes tasks tags spaces]} (form/from-note note)
        share-props {:note            note
                     :share?          share?
                     :on-share-opened on-share-opened
                     :on-share-closed on-share-closed}]
    (if editing?
      [form/note-form
       {:draft-id       draft-id
        :note           note
        :spaces?        spaces?
        :placeholder    "Jot anything down…"
        :auto-focus?    auto-focus?
        :show-hint?     show-hint?
        :footer-start   [note-card/shared-with share-props]
        :saving?        saving?
        :on-save        on-save
        :on-cancel      on-cancel
        :on-open-entity on-open-entity}]
      [:<>
       [:div.body (or text "")]
       [form/note-extras
        {:editing?       false
         :tags           tags
         ;; Read-only viewer: always show the spaces this note is in (the server
         ;; scopes them to what the viewer can see); no add/remove here anyway.
         :spaces         spaces
         :link-urls      urls
         :contacts       contacts
         :linked-notes   notes
         :linked-tasks   tasks
         :on-open-entity on-open-entity}]
       [note-card/note-footer share-props]])))

;; The editor's yellow paper — hosts inherit it so the surface color lives in
;; one place.
(o/defstyled paper :div
  {:background t/--sticky-yellow-light})

(o/defstyled edit-modal paper
  {:border-radius "16px"
   :overflow "hidden"
   :display "flex" :flex-direction "column"}
  [:.modal-body
   {:padding "20px 24px 18px"
    :display "flex" :flex-direction "column" :gap "16px"}]
  ;; Grows with its content and never scrolls itself — the dialog's own overlay
  ;; scroll carries a long note, the way the task composer's details box does.
  ;; Capping it instead (a max-height with overflow-y) put a scrollbar inside a
  ;; modal that was already taller than the viewport, so a long note got two.
  [:textarea.body
   {:width "100%" :box-sizing "border-box"
    :background "transparent" :border "none" :outline "none" :resize "none"
    :padding "0" :margin "0"
    :font-family "inherit" :font-size "15px" :font-weight "500" :line-height "1.6"
    :color t/--charcoal
    :min-height "320px"
    :overflow "hidden"
    :field-sizing "content"}
   [:&::placeholder {:color t/--note-sage-55}]])

;; Slide-up entrance for the full-screen editor. Garden keyframes are evaluated
;; at macro time, so garden.stylesheet needs no cljs require here.
#_{:clj-kondo/ignore [:clojure-lsp/unused-public-var]}
(o/defrules edit-screen-in
  (garden.stylesheet/at-keyframes
   :note-edit-screen-in
   [:from {:transform "translateY(100%)"}]
   [:to {:transform "translateY(0)"}]))

(o/defstyled edit-screen paper
  {:position "fixed" :inset "0"
   :z-index "200"
   :display "flex" :flex-direction "column"
   :overscroll-behavior "contain"
   :animation "note-edit-screen-in 300ms cubic-bezier(.2,.7,.3,1)"}
  ;; The full-screen header sizes its buttons (and the ⋮ beside them) up to
  ;; 32px for touch.
  [header/bar
   {:flex "0 0 auto"
    :padding "14px 16px"}
   [:.bar-action-btn {:width "32px" :height "32px"}]
   [sections/kebab-wrap
    [:button
     {:width "32px" :height "32px" :border-radius "999px"
      :color t/--note-kraft}
     [:&:hover {:background t/--note-kraft-10 :color t/--note-kraft}]
     [:svg {:width "20px" :height "20px"}]]]]
  [:.edit-body
   {:flex "1 1 auto" :min-height "0"
    :padding "16px"
    :display "flex" :flex-direction "column" :gap "14px"}]
  [:textarea.body
   {:width "100%" :box-sizing "border-box"
    :background "transparent" :border "none" :outline "none" :resize "none"
    :padding "0" :margin "0"
    :font-family "inherit" :font-size "16px" :font-weight "500" :line-height "1.6"
    :color t/--charcoal
    :flex "1 1 auto" :min-height "0"
    :overflow-y "auto"
    :overscroll-behavior "contain"}
   [:&::placeholder {:color t/--note-sage-55}]]
  ;; Tags/links and the footer sit below the flexing text area at natural height.
  [#{sections/extras-wrap sections/add-section-row entity-form/footer
     note-card/note-footer-styled}
   {:flex "0 0 auto"}])

(def ^:private visually-hidden
  {:position "absolute" :width "1px" :height "1px" :padding "0" :margin "-1px"
   :overflow "hidden" :clip "rect(0 0 0 0)" :white-space "nowrap" :border "0"})

(defn dialog
  "Radix dialog shell for [[modal-content]]: a transparent panel so the sticky
   is the only visible surface, with the title kept for a11y.
   `on-close-auto-focus` (optional) takes over where the focus lands as the
   dialog closes — for a host that wants it somewhere other than wherever
   Radix restores it to.
    `align=start` top-anchors the dialog so the editor growing as you type
    expands downward instead of recentring — no jump; the margin-top sits it a
    comfortable way below the viewport top rather than the ~32px `--space-6` gap
    that alignment leaves on its own, overriding only the top of Radix's
    `margin: auto` so it stays horizontally centred."
  [{:keys [open? on-close on-close-auto-focus title]} & children]
  [c/DialogRoot
   {:open open?
    :onOpenChange (fn [opening?] (when-not opening? (on-close)))}
   (into [c/DialogContent (cond-> {:align "start"
                                   :style {:background "transparent" :box-shadow "none"
                                           :padding "0" :max-width "900px" :overflow "visible"
                                           :margin-top "10vh"}}
                            on-close-auto-focus (assoc :onCloseAutoFocus on-close-auto-focus))
          [c/DialogTitle {:style visually-hidden} title]]
         children)])

(defn modal-content
  "The editor surface — in a dialog or straight on a page. `on-save` receives
   the composer's staged payload; the ⋮ (Copy URL, plus Delete when
   `on-delete` is given — disabled for anyone but the owner) shows once the
   note exists; the ✕ shows when `on-close` is given.
   `:editing?`/`:share?` (default true) render the surface read-only / hide
   the share button. `:spaces?` (default false) gates the spaces half of the
   tag row."
  [{:keys [title note draft-id editing? share? spaces? auto-focus? show-hint? saving? on-save on-cancel
           on-open-entity on-share-opened on-share-closed on-close on-delete]
    :or {editing? true share? true auto-focus? true}}]
  [edit-modal
   [header/bar {:title title}
    (when share?
      [note-card/share-button {:note            note
                               :on-share-opened on-share-opened
                               :on-share-closed on-share-closed}])
    (when (:note/uuid note)
      [kebab/menu
       {:copy-url       (kebab/app-url "notes" (:note/uuid note))
        :on-delete      on-delete
        :delete-label   "Delete note"
        :confirm-prompt "Delete this note?"
        :delete-disabled-reason (when-not (sharing/owner? note)
                                  "Only the owner can delete this note")
        :aria-label     "Note actions"}])
    (when on-close
      [frame/action-button {:label "Close"
                            :icon [icons/outlined-close {:size 16}]
                            :on-click on-close}])]
   [:div.modal-body
    [body
     {:note            note
      :draft-id        draft-id
      :editing?        editing?
      :share?          share?
      :spaces?         spaces?
      :auto-focus?     auto-focus?
      :show-hint?      show-hint?
      :saving?         saving?
      :on-save         on-save
      :on-cancel       on-cancel
      :on-open-entity  on-open-entity
      :on-share-opened on-share-opened
      :on-share-closed on-share-closed}]]])

(defn screen
  "The full-screen (mobile) editor surface — same contract as
   [[modal-content]]. A fixed overlay, not a dialog: it holds the page body
   still while mounted so nothing behind it scrolls."
  [{:keys [title note draft-id editing? share? spaces? auto-focus? show-hint? saving? on-save on-cancel
           on-open-entity on-share-opened on-share-closed on-close on-delete]
    :or {editing? true share? true auto-focus? true}}]
  (r/with-let [!body-overflow (atom nil)
               ;; A ref callback, not a binding: React detaches the old
               ;; element before it attaches the new, so a keyed remount
               ;; restores and re-locks the body scroll in that order —
               ;; bindings run at render, before the old instance unmounts.
               lock-body-scroll! (fn [el]
                                   (let [body-style (.. js/document -body -style)]
                                     (if el
                                       (do (reset! !body-overflow (.-overflow body-style))
                                           (set! (.-overflow body-style) "hidden"))
                                       (set! (.-overflow body-style) @!body-overflow))))]
    [edit-screen {:ref lock-body-scroll!}
     [header/bar {:title title :dotted? true}
      (when share?
        [note-card/share-button {:note            note
                                 :on-share-opened on-share-opened
                                 :on-share-closed on-share-closed}])
      (when (:note/uuid note)
        [kebab/menu
         {:copy-url       (kebab/app-url "notes" (:note/uuid note))
          :on-delete      on-delete
          :delete-label   "Delete note"
          :confirm-prompt "Delete this note?"
          :delete-disabled-reason (when-not (sharing/owner? note)
                                    "Only the owner can delete this note")
          :aria-label     "Note actions"}])
      [frame/action-button {:label "Close"
                            :icon [icons/outlined-close {:size 20}]
                            :on-click on-close}]]
     [:div.edit-body
      [body
       {:note            note
        :draft-id        draft-id
        :editing?        editing?
        :share?          share?
        :spaces?         spaces?
        :auto-focus?     auto-focus?
        :show-hint?      show-hint?
        :saving?         saving?
        :on-save         on-save
        :on-cancel       on-cancel
        :on-open-entity  on-open-entity
        :on-share-opened on-share-opened
        :on-share-closed on-share-closed}]]]))
